import { type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — Chip / Badge.
 *
 * "`tone`: accent key or neutral. Used for categories, degree levels, file-type
 * badges (`PDF`/`DOC` — dark navy, white text, `radius-sm`)".
 *
 * The accent key is stored on the model (`faculties.accent`,
 * `vocational_programs.accent`) and passed in — §1.4 is explicit that it is
 * never derived from an array index in the frontend, or the colour of a
 * faculty changes when someone reorders a list.
 */
export type ChipTone =
  'neutral' | 'navy' | 'gold' | 'teal' | 'violet' | 'amber' | 'rose' | 'sky' | 'lime' | 'slate';

/**
 * §1.4 pairs every accent with a 50 tint for exactly this.
 *
 * The label is always ink rather than a darkened version of the accent: §1.4
 * defines two values per accent and no third, and the solid 600s are far too
 * light on their own tints (teal #2BA88C on #E6F5F1 is 2.4:1). Inventing eight
 * new hexes to work around that would be inventing design tokens.
 *
 * Losing the coloured label costs nothing here — §7.4 requires the accent be
 * paired with a text label anyway, so the tint is decoration and the word does
 * the work.
 */
const TONES: Record<ChipTone, string> = {
  neutral: 'bg-surface-alt',
  navy: 'bg-accent-tint-navy',
  gold: 'bg-gold-50',
  teal: 'bg-accent-tint-teal',
  violet: 'bg-accent-tint-violet',
  amber: 'bg-accent-tint-amber',
  rose: 'bg-accent-tint-rose',
  sky: 'bg-accent-tint-sky',
  lime: 'bg-accent-tint-lime',
  slate: 'bg-accent-tint-slate',
};

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ChipTone;
  /**
   * Solid dark-navy pill with white text and `radius-sm` — the `PDF`/`DOC`
   * file-type badge from the Documents page.
   */
  variant?: 'tint' | 'solid';
  children?: ReactNode;
}

export function Chip({
  tone = 'neutral',
  variant = 'tint',
  className,
  children,
  ...props
}: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap font-semibold',
        variant === 'solid'
          ? 'rounded-sm bg-navy-800 px-2 py-1 text-caption uppercase text-white'
          : 'rounded-full px-3 py-1 text-caption text-primary',
        variant === 'tint' && TONES[tone],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
